import * as React from 'react'

/**
 * Qual das duas trilhas do programa o visitante está vendo.
 *
 * Porte de ../../../composables/useVisitorTrack.ts. As duas economias do
 * Apre.Partners não se parecem: cliente da Apresenta.me recebe valor fixo por
 * assinatura (e pode virar crédito, que vale mais); parceiro externo recebe uma
 * escada de percentual sempre no PIX. A LP mostrava as duas ao mesmo tempo em
 * toda seção, e o visitante tinha que descobrir sozinho qual metade era dele.
 *
 * `null` NÃO é estado de erro: é "quero ver os dois", que é exatamente a página
 * como ela sempre foi. É também o estado do HTML ESTÁTICO — buscador e visitante
 * sem JavaScript continuam recebendo a página completa. A segmentação acontece
 * depois da hidratação, num efeito.
 *
 * O composable do Nuxt vira Context porque a trilha é lida por uma dúzia de
 * componentes em profundidades diferentes — `useState` do Nuxt já era global.
 *
 * SEM MEMÓRIA ENTRE VISITAS (decisão de 2026-08-07, só nesta build /dev — a LP
 * do Nuxt em produção continua com localStorage). A escolha não é gravada em
 * lugar nenhum: nem localStorage, nem a URL é reescrita depois de escolher.
 * Resultado: toda entrada nova na página — F5 incluído — reabre o portão. A
 * única forma de a página já entrar segmentada é o link já trazer `?perfil=`
 * na URL (campanha/compartilhamento); isso não é "lembrar", é o que a própria
 * URL desta visita diz — por isso continua sendo lido.
 *
 * TROCA DENTRO DA VISITA, PELO HEADER (2026-08-14, revoga o "sem troca" de
 * 2026-08-07). Aquele pedido tirou os dois pontos que reabriam o portão — o
 * link "Trocar" da barra de aviso e o "Não é o meu caso" da calculadora — e o
 * efeito colateral foi que quem clicava errado por pressa ficava preso na
 * economia errada até dar F5, sem nada na página dizendo isso. A saída voltou
 * como controle segmentado permanente no header (components/lp/TrackSwitch.jsx),
 * que chama `choose()` direto.
 *
 * O PORTÃO continua sem reabrir: `gateOpen` só é ligado uma vez, no efeito de
 * montagem, e nada no app o liga de novo. Trocar pelo header é `setTrack`, não
 * "abrir o portão de novo" — a pergunta não é refeita, o visitante só corrige.
 *
 * A persistência também continua fora: trocar não grava nem em localStorage nem
 * na URL, então F5 segue reabrindo o portão com a página limpa.
 */

import { parseVisitorTrack, QUERY_KEY } from '../lib/visitor-track.mjs'

const VisitorTrackContext = React.createContext(null)

export function VisitorTrackProvider({ children }) {
  const [track, setTrack] = React.useState(null)
  const [gateOpen, setGateOpen] = React.useState(false)

  const choose = React.useCallback((value) => {
    setTrack(value)
    setGateOpen(false)
    // Nada é persistido aqui de propósito — nem localStorage nem URL. Ver nota
    // no topo do arquivo.
  }, [])

  /**
   * Resolve a trilha na entrada: só a URL desta visita pode pular o portão.
   * Sem `?perfil=`, o portão abre sempre — não há segunda fonte (localStorage)
   * para consultar. Roda uma vez, depois de montar — nunca durante o render,
   * senão o HTML estático e o primeiro render do cliente discordariam.
   */
  React.useEffect(() => {
    const fromQuery = parseVisitorTrack(new URLSearchParams(window.location.search).get(QUERY_KEY))
    if (fromQuery !== undefined) {
      setTrack(fromQuery)
      return
    }
    setGateOpen(true)
  }, [])

  const value = React.useMemo(
    () => ({
      track,
      gateOpen,
      isClient: track === 'client',
      isPartner: track === 'partner',
      /** Nenhuma trilha escolhida: a página inteira aparece, como antes. */
      showsBoth: track === null,
      /** O cadastro precisa saber a trilha; o "?cliente=1" já existia lá. */
      signupPath: track === 'client' ? '/cadastro?cliente=1' : '/cadastro',
      choose,
    }),
    [track, gateOpen, choose]
  )

  return <VisitorTrackContext.Provider value={value}>{children}</VisitorTrackContext.Provider>
}

export function useVisitorTrack() {
  const ctx = React.useContext(VisitorTrackContext)
  if (!ctx) throw new Error('useVisitorTrack() precisa estar dentro de <VisitorTrackProvider>')
  return ctx
}

/* Reexportado por conveniência de quem já importava daqui. A fonte é
   ../lib/visitor-track.mjs. STORAGE_KEY não é usado neste arquivo (não há
   mais leitura/escrita de localStorage), mas segue definida em
   lib/visitor-track.mjs — só documenta a chave que a LP do Nuxt em produção
   ainda usa; útil se este comportamento for revertido. */
export { parseVisitorTrack, QUERY_KEY }
